import React, { useState, useMemo } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { ArrowUpIcon } from "@heroicons/react/24/solid";
import { ArrowDownTrayIcon, EnvelopeIcon } from "@heroicons/react/24/outline";
import { ColumnDef } from "@tanstack/react-table";
import { toast } from "react-toastify";
import Loader from "../common/Loader";
import Table from "../common/Table";
import EmailReportModal from "../modals/EmailReportModal";
import {
  useScreenOnOffReport,
  useScreenOffHistoryInfinite,
  useOffScreensListInfinite,
  useScreensWithoutSchedulesListInfinite,
} from "../../hooks/useReports";
import {
  OffScreenInfo,
  ScreenWithoutScheduleInfo,
  ScreenOffHistoryEntry,
  ReportColumnFilter,
  OffScreenFilterField,
  NoScheduleFilterField,
  OffHistoryFilterField,
  ReportFormat,
} from "../../schemas/reportSchema";
import { reportService } from "../../api/services/reportService";
import { DISPLAY_TZ, toDisplayDateInput } from "../../utils/dateUtils";

// Formats a duration in minutes into a compact "2d 5h" / "4h 40m" / "35m" label.
const formatDuration = (minutes?: number | null): string => {
  if (minutes == null || minutes < 0) return "-";
  if (minutes < 1) return "0m";
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  // Omit trailing minutes once we're showing days, to keep the label short.
  if (mins && !days) parts.push(`${mins}m`);
  return parts.join(" ") || "0m";
};

// Formats an ISO timestamp like "15 Jul 2026, 02:30 PM".
//
// Stays 12-hour rather than reusing the shared 24-hour helper: the OFF AT /
// BACK ON AT column filters match against `to_char(..., 'HH12:MI AM')`
// server-side, so the cell text has to keep that shape to stay searchable.
// The timezone is pinned to match, which is what the filter already assumed.
const formatDateTime = (iso?: string | null): string => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: DISPLAY_TZ,
  });
};

// The tabs render inside NestedPage's 90vh scroll area, under a fixed-height
// summary strip (~150px) plus the page header and tab bar. Sizing the table off
// what's left keeps the tab bar and the first rows on screen instead of pushing
// them below the fold; the floor stops it collapsing on short viewports.
const TABLE_MAX_HEIGHT = "max(300px, calc(90vh - 420px))";

// Table reports its per-column filters as a { [filterKey]: value } map; the
// queries take the { field, value } list the backend enums expect. Blank values
// are dropped so clearing an input stops filtering by that column.
const toColumnFilters = <TField extends string>(
  values: Record<string, string>,
): ReportColumnFilter<TField>[] =>
  Object.entries(values)
    .filter(([, value]) => value && value.trim())
    .map(([field, value]) => ({ field: field as TField, value: value.trim() }));

const OnOffReport: React.FC = () => {
  const [isEmailModalOpen, setIsEmailModalOpen] = useState<boolean>(false);
  const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);
  const [reportFormat, setReportFormat] = useState<ReportFormat>("EXCEL");
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  // Summary + charts describe the whole tenant and are never filtered — only
  // the tabs below them filter, per column.
  const {
    reportData,
    isLoading,
    error,
    refetch: refetchSummary,
  } = useScreenOnOffReport();
  const [activeTab, setActiveTab] = useState<
    "offScreens" | "noSchedule" | "offHistory"
  >("offScreens");

  // Off History tab state: date range (default last 30 days). Rows load
  // incrementally as the user scrolls; changing a filter re-keys the query so
  // the list restarts from the top on its own.
  const [historyStart, setHistoryStart] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toDisplayDateInput(d);
  });
  const [historyEnd, setHistoryEnd] = useState<string>(() =>
    toDisplayDateInput(),
  );

  // Per-column filters, one map per tab. Each Table owns its own filter inputs
  // and reports changes here (already debounced), and because these live in the
  // query keys the affected list restarts from the top on its own.
  // Off Screen Details scope. The list defaults to outages of at least a day,
  // which is what "chase the screens that are still down" wants; flipping this
  // on adds the ones that went dark within the last 24 hours. Both views only
  // ever contain screens that are off RIGHT NOW — this is about how long, not
  // about whether.
  const [showAllCurrentlyOff, setShowAllCurrentlyOff] = useState(false);
  const offScreensMinOffDays = showAllCurrentlyOff ? 0 : 1;

  const [offScreensFilterValues, setOffScreensFilterValues] = useState<
    Record<string, string>
  >({});
  const [noScheduleFilterValues, setNoScheduleFilterValues] = useState<
    Record<string, string>
  >({});
  const [historyFilterValues, setHistoryFilterValues] = useState<
    Record<string, string>
  >({});

  const offScreensColumnFilters = useMemo(
    () => toColumnFilters<OffScreenFilterField>(offScreensFilterValues),
    [offScreensFilterValues],
  );
  const noScheduleColumnFilters = useMemo(
    () => toColumnFilters<NoScheduleFilterField>(noScheduleFilterValues),
    [noScheduleFilterValues],
  );
  const historyColumnFilters = useMemo(
    () => toColumnFilters<OffHistoryFilterField>(historyFilterValues),
    [historyFilterValues],
  );

  const historyFilters = useMemo(
    () => ({
      startDate: historyStart || null,
      endDate: historyEnd || null,
      columnFilters: historyColumnFilters,
    }),
    [historyStart, historyEnd, historyColumnFilters],
  );

  const {
    items: offScreensList,
    totalCount: offScreensTotalCount,
    isLoading: isOffScreensLoading,
    hasNextPage: hasMoreOffScreens,
    isFetchingNextPage: isFetchingMoreOffScreens,
    fetchNextPage: fetchMoreOffScreens,
  } = useOffScreensListInfinite(
    // Part of the query key, so flipping the toggle restarts the list from the
    // top rather than appending a differently-scoped page to the loaded rows.
    {
      columnFilters: offScreensColumnFilters,
      minOffDays: offScreensMinOffDays,
    },
    activeTab === "offScreens",
  );

  const {
    items: screensWithoutSchedulesList,
    totalCount: noScheduleTotalCount,
    isLoading: isNoScheduleLoading,
    hasNextPage: hasMoreNoSchedule,
    isFetchingNextPage: isFetchingMoreNoSchedule,
    fetchNextPage: fetchMoreNoSchedule,
  } = useScreensWithoutSchedulesListInfinite(
    { columnFilters: noScheduleColumnFilters },
    activeTab === "noSchedule",
  );

  const {
    entries: historyEntries,
    totalCount: historyTotalCount,
    ongoingCount: historyOngoingCount,
    totalOffMinutes: historyTotalOffMinutes,
    isLoading: isHistoryLoading,
    hasNextPage: hasMoreHistory,
    isFetchingNextPage: isFetchingMoreHistory,
    fetchNextPage: fetchMoreHistory,
  } = useScreenOffHistoryInfinite(historyFilters, activeTab === "offHistory");

  // Switching tabs unmounts the previous Table, and Table keeps its column
  // filter inputs in local state — so the filters have to be cleared alongside
  // it, or a tab would keep filtering by values whose inputs render empty.
  const handleTabChange = (tab: "offScreens" | "noSchedule" | "offHistory") => {
    setActiveTab(tab);
    setOffScreensFilterValues({});
    setNoScheduleFilterValues({});
    setHistoryFilterValues({});
    // Opening a tab enables its query, which fetches immediately. Refresh the
    // summary at the same moment so the charts and cards above describe the
    // same instant as the rows that just loaded, rather than whenever the page
    // happened to mount.
    refetchSummary();
  };

  // The header Download/Email buttons act on whichever tab is open, and each
  // tab has its own file: the off-screens snapshot, the no-schedule list, and
  // the per-outage log (date range + filters). They are genuinely separate
  // exports — the on/off report file has only ever rendered the off-screens
  // table, so the no-schedule rows had nowhere to appear in it.
  const isHistoryTab = activeTab === "offHistory";
  const isNoScheduleTab = activeTab === "noSchedule";
  const canExportCurrentTab = isHistoryTab
    ? historyTotalCount > 0
    : isNoScheduleTab
      ? noScheduleTotalCount > 0
      : (reportData?.totalOffScreens ?? 0) > 0;

  const downloadLabel = isHistoryTab
    ? "Off History"
    : isNoScheduleTab
      ? "Screens Without Schedules"
      : "On/Off Report";

  const handleDownload = async (format: ReportFormat) => {
    if (!canExportCurrentTab) {
      toast.error("No data available to download");
      return;
    }

    try {
      toast.info(`Generating ${format} report...`);

      const downloadResult = isHistoryTab
        ? await reportService.downloadScreenOffHistory({
            ...historyFilters,
            format,
          })
        : isNoScheduleTab
          ? await reportService.downloadScreensWithoutSchedules({
              columnFilters: noScheduleColumnFilters,
              format,
            })
          : await reportService.downloadScreenOnOffReport({
              offScreenColumnFilters: offScreensColumnFilters,
              noScheduleColumnFilters: noScheduleColumnFilters,
              minOffDays: offScreensMinOffDays,
              format,
            });

      // Convert base64 to blob
      const byteCharacters = atob(downloadResult.base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: downloadResult.mimeType });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = downloadResult.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Report downloaded successfully");
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.error("Failed to download report");
    }
  };

  const handleSendEmail = async (email: string, format: ReportFormat) => {
    if (!canExportCurrentTab) {
      toast.error("No report data available to send");
      return;
    }

    setIsSendingEmail(true);
    try {
      if (isHistoryTab) {
        await reportService.sendScreenOffHistoryReportEmail(email, {
          ...historyFilters,
          format,
        });
      } else if (isNoScheduleTab) {
        await reportService.sendScreensWithoutSchedulesReportEmail(email, {
          columnFilters: noScheduleColumnFilters,
          format,
        });
      } else {
        await reportService.sendScreenOnOffReportEmail(email, {
          offScreenColumnFilters: offScreensColumnFilters,
          noScheduleColumnFilters: noScheduleColumnFilters,
          minOffDays: offScreensMinOffDays,
          format,
        });
      }
      toast.success("Report sent successfully via email");
      setIsEmailModalOpen(false);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email");
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Column definitions for Off Screens table. Sorting is disabled because rows
  // are server-ordered (longest outage first) across all pages — a client-side
  // sort would only reorder the pages loaded so far. Each `meta.filterKey` is
  // sent verbatim as the backend's OffScreenFilterField enum value.
  const offScreensColumns: ColumnDef<OffScreenInfo>[] = useMemo(
    () => [
      {
        accessorKey: "screenName",
        header: "SCREEN NAME",
        enableSorting: false,
        meta: { filterKey: "SCREEN_NAME" },
      },
      {
        accessorKey: "region",
        header: "REGION",
        enableSorting: false,
        meta: { filterKey: "REGION" },
        cell: ({ row }) => row.original.region || "-",
      },
      {
        accessorKey: "orientation",
        header: "ORIENTATION",
        enableSorting: false,
        meta: { filterKey: "ORIENTATION" },
        cell: ({ row }) => row.original.orientation || "-",
      },
      {
        accessorKey: "city",
        header: "City",
        enableSorting: false,
        meta: { filterKey: "CITY" },
        cell: ({ row }) => row.original.city || "-",
      },
      {
        accessorKey: "offForDays",
        header: "Off For (days)",
        enableSorting: false,
        // Numeric column: the server matches it exactly, so "7" doesn't also
        // return 17 and 70.
        meta: { filterKey: "OFF_FOR_DAYS", filterPlaceholder: "e.g. 7" },
      },
    ],
    [],
  );

  // Column definitions for Screens Without Schedules table (server-ordered by
  // screen name across all pages, so client-side sorting stays off).
  const noScheduleColumns: ColumnDef<ScreenWithoutScheduleInfo>[] = useMemo(
    () => [
      {
        accessorKey: "screenName",
        header: "SCREEN NAME",
        enableSorting: false,
        meta: { filterKey: "SCREEN_NAME" },
      },
      {
        accessorKey: "region",
        header: "REGION",
        enableSorting: false,
        meta: { filterKey: "REGION" },
        cell: ({ row }) => row.original.region || "-",
      },
      {
        accessorKey: "orientation",
        header: "ORIENTATION",
        enableSorting: false,
        meta: { filterKey: "ORIENTATION" },
        cell: ({ row }) => row.original.orientation || "-",
      },
      {
        accessorKey: "city",
        header: "City",
        enableSorting: false,
        meta: { filterKey: "CITY" },
        cell: ({ row }) => row.original.city || "-",
      },
    ],
    [],
  );

  // Column definitions for the Off History (per-outage) table. Sorting is
  // disabled because rows are server-ordered newest-first across all pages.
  // DURATION is left unfiltered on purpose: it renders as "2d 5h", which a
  // "contains" filter can't match usefully — filter the timestamps instead.
  const historyColumns: ColumnDef<ScreenOffHistoryEntry>[] = useMemo(
    () => [
      {
        accessorKey: "screenName",
        header: "SCREEN NAME",
        enableSorting: false,
        meta: { filterKey: "SCREEN_NAME" },
      },
      {
        accessorKey: "region",
        header: "REGION",
        enableSorting: false,
        meta: { filterKey: "REGION" },
        cell: ({ row }) => row.original.region || "-",
      },
      {
        accessorKey: "city",
        header: "CITY",
        enableSorting: false,
        meta: { filterKey: "CITY" },
        cell: ({ row }) => row.original.city || "-",
      },
      {
        accessorKey: "orientation",
        header: "ORIENTATION",
        enableSorting: false,
        meta: { filterKey: "ORIENTATION" },
        cell: ({ row }) => row.original.orientation || "-",
      },
      {
        id: "offTimeStart",
        header: "OFF AT",
        enableSorting: false,
        meta: { filterKey: "OFF_AT", filterPlaceholder: "dd-mm-yyyy" },
        cell: ({ row }) => formatDateTime(row.original.offTimeStart),
      },
      {
        id: "offTimeEnd",
        header: "BACK ON AT",
        enableSorting: false,
        meta: {
          filterKey: "BACK_ON_AT",
          filterPlaceholder: "dd-mm-yyyy / ongoing",
        },
        cell: ({ row }) =>
          row.original.offTimeEnd ? (
            formatDateTime(row.original.offTimeEnd)
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              Currently Off
            </span>
          ),
      },
      {
        id: "duration",
        header: "DURATION",
        enableSorting: false,
        cell: ({ row }) => formatDuration(row.original.totalOffTime),
      },
    ],
    [],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading report: {error.message}</p>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-600">No report data available</p>
        </div>
      </div>
    );
  }

  const {
    totalScreens,
    totalOnScreens,
    totalOffScreens,
    totalScreensWithoutSchedules,
    screensOffThanPreviousDay,
    screensOffForMoreThanWeek,
    previous7DaysGraph,
  } = reportData;

  // Transform graph data for recharts (bar chart format)
  const barChartData = previous7DaysGraph.map((point) => {
    const date = new Date(point.date);
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "short" });

    // Format: Just date like "1 Jan", "2 Jan"
    return {
      date: `${day} ${month}`, // e.g., "1 Jan", "2 Jan"
      offScreens: point.offScreens,
    };
  });

  // Pie chart data for on/off screens
  const pieData = [
    {
      name: "On",
      value: totalOnScreens,
    },
    {
      name: "Off",
      value: totalOffScreens,
    },
  ];

  const COLORS = ["#22c55e", "#ef4444"]; // success-default, danger-default

  return (
    <div className="px-6 py-3 space-y-4">
      {/* Header with Action Buttons */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">On/Off Report</h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              type="button"
              className="p-2 bg-primary-default text-white rounded hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed"
              title={`Download ${downloadLabel}`}
              disabled={!canExportCurrentTab}
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
            </button>
            {showDownloadMenu && (
              <div className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <button
                  className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-t-lg text-left"
                  onClick={() => {
                    setShowDownloadMenu(false);
                    setReportFormat("PDF");
                    handleDownload("PDF");
                  }}
                >
                  Download PDF
                </button>
                <button
                  className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-b-lg text-left"
                  onClick={() => {
                    setShowDownloadMenu(false);
                    setReportFormat("EXCEL");
                    handleDownload("EXCEL");
                  }}
                >
                  Download Excel
                </button>
              </div>
            )}
          </div>
          <button
            className="p-2 bg-primary-default text-white rounded hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed"
            title={`Mail ${downloadLabel}`}
            onClick={() => setIsEmailModalOpen(true)}
            disabled={!canExportCurrentTab}
          >
            <EnvelopeIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Summary strip. Kept deliberately short (~150px) so the tabs and the
          first table rows land above the fold — the tables are what this page
          is used for, the charts are the glance. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Section 1: On/Off donut, with the counts as its legend. The slice
            labels the pie used to draw are dropped: the legend already carries
            the same numbers, and losing them lets the chart shrink. */}
        <div className="bg-white rounded-lg shadow px-4 py-3">
          <div className="flex items-center gap-4">
            <div className="shrink-0">
              <PieChart width={124} height={124}>
                <Pie
                  data={pieData}
                  cx={61}
                  cy={61}
                  innerRadius={36}
                  outerRadius={58}
                  paddingAngle={2}
                  stroke="none"
                  dataKey="value"
                >
                  {pieData.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </div>

            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-primary-default shrink-0" />
                <span className="text-xs text-gray-600">Total Screens</span>
                <span className="ml-auto text-sm font-bold text-gray-900">
                  {totalScreens}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-success-default shrink-0" />
                <span className="text-xs text-gray-600">Screens On</span>
                <span className="ml-auto text-sm font-bold text-gray-900">
                  {totalOnScreens}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-danger-default shrink-0" />
                <span className="text-xs text-gray-600">Screens Off</span>
                <span className="ml-auto text-sm font-bold text-gray-900">
                  {totalOffScreens}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Bar Chart - No. of screens OFF since. Labels sit flat
            rather than rotated 45°, which is what used to cost 80px of axis. */}
        <div className="bg-white rounded-lg shadow px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-800">
            No. of screens OFF since
          </h2>
          <ResponsiveContainer width="100%" height={104}>
            <BarChart
              data={barChartData}
              margin={{ top: 8, right: 4, bottom: 0, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                interval={0}
                height={16}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 10 }}
              />
              <YAxis
                allowDecimals={false}
                domain={[0, "dataMax + 10"]}
                width={30}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 10 }}
              />
              <Tooltip />
              <Bar dataKey="offScreens" fill="#1d4ed8" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Section 3: Summary Cards. Number and caption sit on one line so the
            three of them stack into the same height as the charts. */}
        <div className="flex flex-col gap-2">
          {/* More OFF than previous day */}
          <div className="flex flex-1 items-center gap-2 bg-white rounded-lg shadow px-3 py-2">
            <ArrowUpIcon className="h-4 w-4 text-danger-default shrink-0" />
            <p className="text-xl font-bold text-danger-default">
              {screensOffThanPreviousDay > 0 ? "+" : ""}
              {screensOffThanPreviousDay}
            </p>
            <p className="text-xs text-gray-600">More OFF than previous day</p>
          </div>

          {/* Screens with no schedule */}
          <div className="flex flex-1 items-center gap-2 bg-white rounded-lg shadow px-3 py-2">
            <p className="text-xl font-bold text-danger-default">
              {totalScreensWithoutSchedules}
            </p>
            <p className="text-xs text-gray-600">Screens with no schedule</p>
          </div>

          {/* Screens off for more than a week */}
          <div className="flex flex-1 items-center gap-2 bg-white rounded-lg shadow px-3 py-2">
            <p className="text-xl font-bold text-danger-default">
              {screensOffForMoreThanWeek}
            </p>
            <p className="text-xs text-gray-600">
              Screens off for more than a week
            </p>
          </div>
        </div>
      </div>

      {/* Tabs and Tables Section */}
      <div className="bg-white rounded-lg shadow">
        {/* Tabs */}
        <div className="flex border-b-2 border-primary-default">
          <button
            onClick={() => handleTabChange("offScreens")}
            className={`px-6 py-2 font-medium transition-colors duration-150 ${
              activeTab === "offScreens"
                ? "text-white bg-primary-default"
                : "text-gray-600 bg-gray-100 hover:bg-gray-200"
            }`}
          >
            Off Screen Details
          </button>
          <button
            onClick={() => handleTabChange("noSchedule")}
            className={`px-6 py-2 font-medium transition-colors duration-150 ${
              activeTab === "noSchedule"
                ? "text-white bg-primary-default"
                : "text-gray-600 bg-gray-100 hover:bg-gray-200"
            }`}
          >
            Screens with no schedule
          </button>
          <button
            onClick={() => handleTabChange("offHistory")}
            className={`px-6 py-2 font-medium transition-colors duration-150 ${
              activeTab === "offHistory"
                ? "text-white bg-primary-default"
                : "text-gray-600 bg-gray-100 hover:bg-gray-200"
            }`}
          >
            Off History
          </button>
        </div>

        {/* Table Content */}
        <div className="p-4">
          {activeTab === "offScreens" && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-gray-600">
                  Off screens:{" "}
                  <span className="font-bold text-gray-900">
                    {offScreensTotalCount}
                  </span>
                </div>

                {/* Both options list screens that are off right now; they differ
                    only in whether outages under 24 hours are included. */}
                <div
                  className="inline-flex rounded-lg border border-gray-300 p-0.5"
                  role="group"
                  aria-label="Off screens scope"
                >
                  <button
                    type="button"
                    onClick={() => setShowAllCurrentlyOff(false)}
                    aria-pressed={!showAllCurrentlyOff}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      showAllCurrentlyOff
                        ? "text-gray-600 hover:bg-gray-100"
                        : "bg-blue-600 text-white"
                    }`}
                  >
                    Off over a day
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAllCurrentlyOff(true)}
                    aria-pressed={showAllCurrentlyOff}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      showAllCurrentlyOff
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    All currently off
                  </button>
                </div>
              </div>
              <Table
                data={offScreensList}
                columns={offScreensColumns}
                searchable={false}
                pagination={false}
                isLoading={isOffScreensLoading}
                maxHeight={TABLE_MAX_HEIGHT}
                infinite
                hasNextPage={hasMoreOffScreens}
                isFetchingMore={isFetchingMoreOffScreens}
                onLoadMore={() => fetchMoreOffScreens()}
                columnFiltersEnabled
                onColumnFilterChange={setOffScreensFilterValues}
              />
            </div>
          )}

          {activeTab === "noSchedule" && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                Screens with no schedule:{" "}
                <span className="font-bold text-gray-900">
                  {noScheduleTotalCount}
                </span>
              </div>
              <Table
                data={screensWithoutSchedulesList}
                columns={noScheduleColumns}
                searchable={false}
                pagination={false}
                isLoading={isNoScheduleLoading}
                maxHeight={TABLE_MAX_HEIGHT}
                infinite
                hasNextPage={hasMoreNoSchedule}
                isFetchingMore={isFetchingMoreNoSchedule}
                onLoadMore={() => fetchMoreNoSchedule()}
                columnFiltersEnabled
                onColumnFilterChange={setNoScheduleFilterValues}
              />
            </div>
          )}

          {activeTab === "offHistory" && (
            <div className="space-y-4">
              {/* Date range + summary strip */}
              <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex flex-col">
                    <label className="text-xs text-gray-600 mb-1">From</label>
                    <input
                      type="date"
                      value={historyStart}
                      max={historyEnd || undefined}
                      onChange={(e) => setHistoryStart(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-default"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs text-gray-600 mb-1">To</label>
                    <input
                      type="date"
                      value={historyEnd}
                      min={historyStart || undefined}
                      onChange={(e) => setHistoryEnd(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-default"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <span className="text-gray-600">
                    Outages:{" "}
                    <span className="font-bold text-gray-900">
                      {historyTotalCount}
                    </span>
                  </span>
                  <span className="text-gray-600">
                    Currently Off:{" "}
                    <span className="font-bold text-danger-default">
                      {historyOngoingCount}
                    </span>
                  </span>
                  <span className="text-gray-600">
                    Total downtime:{" "}
                    <span className="font-bold text-gray-900">
                      {formatDuration(historyTotalOffMinutes)}
                    </span>
                  </span>
                </div>
              </div>

              <Table
                data={historyEntries}
                columns={historyColumns}
                searchable={false}
                pagination={true}
                isLoading={isHistoryLoading}
                maxHeight={TABLE_MAX_HEIGHT}
                infinite
                hasNextPage={hasMoreHistory}
                isFetchingMore={isFetchingMoreHistory}
                onLoadMore={() => fetchMoreHistory()}
                columnFiltersEnabled
                onColumnFilterChange={setHistoryFilterValues}
              />
            </div>
          )}
        </div>
      </div>

      {/* Email Modal */}
      {isEmailModalOpen && (
        <EmailReportModal
          isOpen={isEmailModalOpen}
          onClose={() => setIsEmailModalOpen(false)}
          onSubmit={handleSendEmail}
          isLoading={isSendingEmail}
          reportName={isHistoryTab ? "Screen Off History" : "On/Off Report"}
          defaultFormat={reportFormat}
        />
      )}
    </div>
  );
};

export default OnOffReport;
